import { useCallback, useMemo, useReducer } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import {
  coverEditorReducer,
  initialCoverEditorState,
  type CoverEditorAction,
  type CoverEditorState,
} from './coverEditorReducer';

/** An action with no time: the hook adds the clock. */
export type CoverEditorDispatch = (action: CoverEditorAction) => void;

export interface CoverEditorDoc {
  state: CoverEditorState;
  /** Sends a change. A change with a `coalesce` key gets the time of now. */
  dispatch: CoverEditorDispatch;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * The document of the open design with Undo and Redo (`coverEditorReducer`). The parent gives a
 * new `key` for another design, so the history starts new with it.
 */
export function useCoverEditorDoc(initialDoc: CoverDoc): CoverEditorDoc {
  const [state, rawDispatch] = useReducer(coverEditorReducer, initialDoc, initialCoverEditorState);
  const dispatch = useCallback<CoverEditorDispatch>((action) => {
    rawDispatch('coalesce' in action && action.coalesce !== undefined ? { ...action, at: Date.now() } : action);
  }, []);
  return useMemo(
    () => ({ state, dispatch, canUndo: state.past.length > 0, canRedo: state.future.length > 0 }),
    [dispatch, state]
  );
}
